import { AdminShell } from '../components/AdminShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { useAdminMetrics } from '../useAdminMetrics';
import { cn } from '@/lib/cn';

interface RevenueMetrics {
  generated_at: string;
  paying_users: { paying_users: number } | null;
  mrr_today: { mrr_aud: number; premium_count: number; vip_count: number } | null;
  conversion_funnel_24h: {
    plans_view: number;
    checkout_started: number;
    checkout_completed: number;
  } | null;
  churn_30d: { churned_30d: number } | null;
}

const fmt = (n: number | undefined | null) =>
  typeof n === 'number' ? n.toLocaleString() : '—';
const aud = (n: number | undefined | null) =>
  typeof n === 'number' ? `A$${n.toLocaleString()}` : '—';

const Card = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className={cn('border border-white/[0.08] bg-charcoal/40 p-5')}>
    <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">{label}</p>
    <p className="mt-2 font-display text-3xl font-black uppercase tracking-display text-text-primary">{value}</p>
    {sub && <p className="mt-2 text-[11px] text-text-secondary">{sub}</p>}
  </div>
);

export const AdminRevenuePage = () => {
  const { data, loading, error } = useAdminMetrics<RevenueMetrics>('revenue');
  const m = data;
  const funnel = m?.conversion_funnel_24h;
  const conv = funnel && funnel.plans_view > 0
    ? `${Math.round((funnel.checkout_completed / funnel.plans_view) * 100)}%`
    : '—';

  return (
    <AdminShell eyebrow="Moderation · revenue">
      <div className="px-5 py-7 md:px-12 md:py-10">
        <SectionMarker className="mb-3">Subscription health</SectionMarker>
        <h1 className="font-display text-3xl font-black uppercase tracking-display text-text-primary md:text-4xl">
          The till<span className="text-pink">.</span>
        </h1>
        {m?.generated_at && (
          <p className="mt-2 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Updated {new Date(m.generated_at).toLocaleString()}
          </p>
        )}
        {error && (
          <p className="mt-4 font-display text-[10px] font-bold uppercase tracking-widest text-pink">— {error}</p>
        )}

        {loading && !m ? (
          <p className="mt-6 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Loading<span className="text-pink">.</span>
          </p>
        ) : (
          <>
            <div className="mt-8 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <Card label="Paying users" value={fmt(m?.paying_users?.paying_users)} />
              <Card
                label="MRR today"
                value={aud(m?.mrr_today?.mrr_aud)}
                sub={`${fmt(m?.mrr_today?.premium_count)} premium · ${fmt(m?.mrr_today?.vip_count)} VIP`}
              />
              <Card label="Churned · 30d" value={fmt(m?.churn_30d?.churned_30d)} />
              <Card label="Plans → checkout conv · 24h" value={conv} />
            </div>

            <SectionMarker className="mb-3 mt-10">Conversion funnel · 24h</SectionMarker>
            <div className="grid gap-3 md:grid-cols-3">
              <Card label="Plans page views"    value={fmt(funnel?.plans_view)} />
              <Card label="Checkouts started"   value={fmt(funnel?.checkout_started)} />
              <Card label="Checkouts completed" value={fmt(funnel?.checkout_completed)} />
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
};
